//src\components\student\ai\AIReport.js
import React, { useState, useEffect } from 'react';
import Preloader from '../../Preloader';
import { doc, getDoc } from 'firebase/firestore';
import { getDatabase, ref, get } from 'firebase/database';
import { db } from '../../../firebase';


const AIReport = ({ classId, user }) => {
  const [loading, setLoading] = useState(true);
  const [reportsBySession, setReportsBySession] = useState({});
  const [sessionsMeta, setSessionsMeta] = useState({});
  const [expandedSessions, setExpandedSessions] = useState({});
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // 1. Fetch AI reports for this student
        const aiSnap = await getDoc(doc(db, 'AIReport', classId));
        const aiData = aiSnap.exists() ? aiSnap.data()[user.uid] || {} : {};
        setReportsBySession(aiData);

        // 2. Fetch session metadata (for date/time display & filtering)
        const dbRT = getDatabase();
        const sessSnap = await get(ref(dbRT, `sessions/${classId}`));
        const sessData = sessSnap.exists() ? sessSnap.val() : {};
        setSessionsMeta(sessData);
      } catch (err) {
        console.error('Error loading AI report data:', err);
        setReportsBySession({});
        setSessionsMeta({});
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [classId, user.uid]);

  const toggleSession = (sessionId) => {
    setExpandedSessions(prev => ({
      ...prev,
      [sessionId]: !prev[sessionId],
    }));
  };

  if (loading) return <Preloader />;

  const sessionIds = Object.keys(reportsBySession);

  // Filter by date string (local date)
  const filteredSessionIds = sessionIds.filter(sessionId => {
    const meta = sessionsMeta[sessionId] || {};
    const dateOnly = meta.start_date_time
      ? new Date(meta.start_date_time).toLocaleDateString()
      : sessionId;
    return dateOnly.includes(searchTerm);
  });

  if (filteredSessionIds.length === 0) {
    return (
      <div className="px-4 py-4">
        <input
          type="text"
          placeholder="Search Sessions..."
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          className="w-full p-2 mb-4 border border-[var(--muted-color)] rounded bg-[var(--card-bg)] text-[var(--text-color)] focus:outline-none focus:ring-2 focus:ring-[var(--primary-color)] focus:border-transparent transition-all"
          style={{
            backgroundColor: 'var(--card-bg)',
            color: 'var(--text-color)',
            borderColor: 'var(--muted-color)'
          }}
        />
        <p className="text-[var(--muted-color)]">
          No AI reports match your search.
        </p>
      </div>
    );
  }

  return (
    <div className="px-4 py-4">
      <h3
        className="text-xl font-semibold mb-4"
        style={{ color: 'var(--text-color)' }}
      >
        AI Reports
      </h3>

      <input
        type="text"
        placeholder="Search Sessions..."
        value={searchTerm}
        onChange={e => setSearchTerm(e.target.value)}
        className="w-full p-2 mb-4 border border-[var(--muted-color)] rounded bg-[var(--card-bg)] text-[var(--text-color)] focus:outline-none focus:ring-2 focus:ring-[var(--primary-color)] focus:border-transparent transition-all"
        style={{
          backgroundColor: 'var(--card-bg)',
          color: 'var(--text-color)',
          borderColor: 'var(--muted-color)'
        }}
      />

      <div className="space-y-4">
        {filteredSessionIds.map((sessionId) => {
          const { Status, Report } = reportsBySession[sessionId];
          const meta = sessionsMeta[sessionId] || {};
          const dateStr = meta.start_date_time
            ? new Date(meta.start_date_time).toLocaleString()
            : sessionId;

          return (
            <div
              key={sessionId}
              className="border rounded shadow-sm"
              style={{ borderColor: 'var(--muted-color)' }}
            >
              {/* Session Header */}
              <div
                className="flex justify-between items-center p-3 rounded cursor-pointer"
                style={{ backgroundColor: 'var(--card-hover-bg)' }}
                onClick={() => toggleSession(sessionId)}
              >
                <h4
                  className="text-lg font-medium"
                  style={{ color: 'var(--text-color)' }}
                >
                  Session on {dateStr}
                </h4>
                <span
                  className="text-xl"
                  style={{ color: 'var(--text-color)' }}
                >
                  {expandedSessions[sessionId] ? '▴' : '▾'}
                </span>
              </div>

              {/* Expanded Content */}
              {expandedSessions[sessionId] && (
                <div className="p-4" style={{ backgroundColor: 'var(--card-bg)' }}>
                  {Status === 'Successful' ? (
                    Array.isArray(Report) && Report.length > 0 ? (
                      Report.map((item, idx) => (
                        <div key={idx} className="mb-4 p-4 border rounded" style={{ borderColor: 'var(--muted-color)' }}>
                          <h5 className="text-lg font-semibold mb-2" style={{ color: 'var(--text-color)' }}>
                            {item.Title}
                          </h5>
                          <p style={{ color: 'var(--text-color)' }}>{item.Paragraph}</p>
                        </div>
                      ))
                    ) : (
                      <p style={{ color: 'var(--text-color)' }}>
                        No report content available.
                      </p>
                    )
                  ) : (
                    <p style={{ color: 'var(--text-color)' }}>
                      <strong>Status:</strong> {Status}
                    </p>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default AIReport;